import React, { useState, useEffect } from 'react';
import moment from 'moment';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import {
  previewBroadcastCohort,
  listBroadcasts,
  recordBroadcast,
  updateBroadcast,
  cancelBroadcast,
  sendBroadcastTestText,
  getBroadcastRecipients,
  retryBroadcastFailures,
  getConciergeRequestCodes,
  getConciergeEditors,
  getCohortBrandFacets
} from '../../api/noir';
import { Spinner } from './Broadcast/primitives';
import { useBroadcastFilters } from './Broadcast/useBroadcastFilters';
import { adaptBroadcast } from './Broadcast/helpers';
import CohortBuilder from './Broadcast/CohortBuilder';
import CohortPreview from './Broadcast/CohortPreview';
import MessageComposer from './Broadcast/MessageComposer';
import PastBroadcastsLog from './Broadcast/PastBroadcastsLog';
import ShopperListSlideOver from './Broadcast/ShopperListSlideOver';
import EditBroadcastSlideOver from './Broadcast/EditBroadcastSlideOver';
import './NoirBroadcast.scss';

const toastError = (err, fallback) =>
  cogoToast.error(typeof err === 'string' ? err : fallback);

const NoirBroadcast = () => {
  // Filter state — owned entirely by the hook, exposed as one `payload` to API callers.
  const filters = useBroadcastFilters();

  // Resource state loaded on mount
  const [promoCodes, setPromoCodes] = useState([]);
  const [editors, setEditors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // Cohort preview lifecycle
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');

  // "Common brands" facet panel — loaded lazily after each successful preview (same filter payload)
  // so the preview itself stays fast. Clicking a chip feeds the brand into filters.brandIds.
  const [brandFacets, setBrandFacets] = useState(null);
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Composer
  const [msg, setMsg] = useState('');
  const [mode, setMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [sendState, setSendState] = useState('idle');

  // Test text
  const [testPhone, setTestPhone] = useState('');
  const [testState, setTestState] = useState('idle');
  const [testError, setTestError] = useState('');

  // Slide-over visibility
  const [showShopperList, setShowShopperList] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [editDraft, setEditDraft] = useState({ message: '', date: '', time: '' });

  useEffect(() => {
    getConciergeRequestCodes()
      .then(res => {
        const list = Array.isArray(res) ? res : res?.codes || res?.data || [];
        setPromoCodes(list.map(c => c.code).filter(Boolean));
      })
      .catch(() => cogoToast.error('Failed to load promo codes.'));
    getConciergeEditors()
      .then(setEditors)
      .catch(() => cogoToast.error('Failed to load editors.'));
    listBroadcasts()
      .then(list => setCampaigns(list.map(adaptBroadcast)))
      .catch(() => cogoToast.error('Failed to load past broadcasts.'));
  }, []);

  // overrides lets callers force a payload field that hasn't yet propagated through the
  // hook's render cycle — e.g. the "exclude these X" button in the recency warning fires
  // setExcludeRecentDays(...) and previews immediately, but filters.payload still
  // reflects the old value until React re-renders.
  const preview = (overrides = {}) => {
    setLoading(true);
    const payload = { ...filters.payload, ...overrides };
    previewBroadcastCohort(payload)
      .then(data => {
        const shoppers = data.shoppers || [];
        setCohort({
          matched: shoppers,
          withPhone: data.withPhone ?? shoppers.length,
          recentlyMessagedCount: data.recentlyMessagedCount || 0,
          recentWindowDays: data.recentWindowDays || 14,
          total: data.total || shoppers.length,
          truncated: !!data.truncated
        });
        setLoading(false);
        setSendState('idle');
        loadBrandFacets(payload);
      })
      .catch(err => {
        setLoading(false);
        toastError(err, 'Failed to preview cohort.');
      });
  };

  // Facet failures are silent (the panel just doesn't render) — the preview itself succeeded and a
  // toast for a secondary discovery panel would be noise.
  const loadBrandFacets = payload => {
    setFacetsLoading(true);
    setBrandFacets(null);
    getCohortBrandFacets(payload)
      .then(data => {
        setBrandFacets(data);
        setFacetsLoading(false);
      })
      .catch(() => setFacetsLoading(false));
  };

  // Toggle a facet chip: add/remove the brand in the purchased-from-brand filter and re-preview.
  const toggleFacetBrand = brandId => {
    const next = filters.brandIds.includes(brandId) ? filters.brandIds.filter(id => id !== brandId) : [...filters.brandIds, brandId];
    filters.setBrandIds(next);
    preview({ brandIds: next });
  };

  // "Clear all" in the builder: wipe every filter AND the previewed cohort, returning the page to
  // its empty build-your-cohort state so a stale preview can't be sent against cleared filters.
  const resetCohort = () => {
    filters.resetFilters();
    setCohort(null);
    setBrandFacets(null);
    setSendState('idle');
  };

  // Duplicate a past broadcast: hydrate the builder with its filter snapshot, message and title,
  // then preview immediately so the admin lands on a ready-to-tweak draft. The snapshot is passed
  // as a preview override since the hook's payload won't reflect the new state until re-render.
  const duplicateBroadcast = c => {
    const snapshot = c.filters || {};
    filters.loadFromSavedFilters(snapshot);
    setMsg(c.rawMessage || '');
    setTitle(c.title || '');
    setMode('now');
    setSendState('idle');
    preview(snapshot);
    cogoToast.info('Loaded broadcast — adjust the cohort or message, then send.');
  };

  const send = () => {
    if (sendState === 'idle') {
      setSendState('confirming');
      return;
    }
    if (sendState !== 'confirming') return;

    setSendState('sending');
    // The schedule inputs are entered in the admin's local time (labeled ET). Persist as UTC so
    // it's consistent with the rest of the system (knex reads/writes DATETIME as UTC) and the
    // worker, which fires off UTC_TIMESTAMP(). Without .utc() the naive local string was stored
    // as-is then re-read as UTC, shifting the displayed/fired time by the local offset.
    const scheduledFor =
      mode === 'schedule' && scheduleDate && scheduleTime
        ? moment(`${scheduleDate} ${scheduleTime}`).utc().format('YYYY-MM-DD HH:mm:ss')
        : null;

    recordBroadcast({
      filters: filters.payload,
      title: title.trim() || null,
      message: msg,
      mode,
      scheduledFor
    })
      .then(() => listBroadcasts())
      .then(list => {
        setCampaigns(list.map(adaptBroadcast));
        setSendState('sent');
        setTimeout(() => setSendState('idle'), 4000);
      })
      .catch(err => {
        setSendState('idle');
        toastError(err, 'Failed to send broadcast.');
      });
  };

  const sendTest = () => {
    const cleaned = testPhone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      setTestError('Enter a valid US phone number');
      return;
    }
    setTestError('');
    setTestState('sending');
    sendBroadcastTestText({ phone: cleaned, body: msg })
      .then(() => {
        setTestState('sent');
        setTimeout(() => setTestState('idle'), 3500);
      })
      .catch(err => {
        setTestState('idle');
        setTestError(typeof err === 'string' ? err : 'Failed to send test text.');
      });
  };

  const openEditBroadcast = c => {
    // Prefill the original time if it's still in the future; otherwise default to 15 min out so a
    // failed/overdue broadcast doesn't open with a past time that fails validation immediately.
    const scheduled = c.scheduledForRaw ? moment(c.scheduledForRaw) : null;
    const m = scheduled && scheduled.isAfter(moment()) ? scheduled : moment().add(15, 'minutes');
    setEditingBroadcast(c);
    setEditDraft({
      message: c.rawMessage || '',
      date: m.format('YYYY-MM-DD'),
      time: m.format('HH:mm')
    });
  };

  const saveEditBroadcast = () => {
    if (!editingBroadcast) return;
    const { message: m, date, time } = editDraft;
    if (!m.trim()) return cogoToast.error('Message cannot be empty.');
    if (!date || !time) return cogoToast.error('Set a date and time.');
    // Validate locally for instant feedback before the round-trip: must be a real datetime in the
    // future. The schedule inputs are local (ET); persist as UTC to match the rest of the system.
    const when = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm', true);
    if (!when.isValid()) return cogoToast.error('Enter a valid date and time.');
    if (!when.isAfter(moment())) return cogoToast.error('Scheduled time must be in the future.');
    updateBroadcast(editingBroadcast.id, {
      message: m,
      scheduledFor: when.utc().format('YYYY-MM-DD HH:mm:ss')
    })
      .then(() => listBroadcasts())
      .then(list => {
        setCampaigns(list.map(adaptBroadcast));
        setEditingBroadcast(null);
        cogoToast.success('Broadcast updated.');
      })
      .catch(err => toastError(err, 'Failed to update broadcast.'));
  };

  const cancelScheduledBroadcast = c => {
    const sending = c.status === 'sending';
    const label = c.title || 'This broadcast';
    confirmAlert({
      title: sending ? 'Stop in-flight broadcast?' : 'Cancel scheduled broadcast?',
      message: sending
        ? `"${label}" will stop sending. Shoppers already texted keep their message; the rest won't be sent.`
        : `"${label}" will not be sent.`,
      buttons: [
        { label: sending ? 'Keep sending' : 'Keep it', onClick: () => {} },
        {
          label: sending ? 'Stop broadcast' : 'Cancel broadcast',
          onClick: () =>
            cancelBroadcast(c.id)
              .then(() => listBroadcasts())
              .then(list => setCampaigns(list.map(adaptBroadcast)))
              .catch(err => toastError(err, 'Failed to cancel broadcast.'))
        }
      ]
    });
  };

  // Retry the transient (send_error) failures of a broadcast, then refresh the row's status/counts
  // and re-attach the fresh per-recipient list so the open drill-down updates in place.
  const retryFailed = c =>
    retryBroadcastFailures(c.id)
      .then(res => {
        cogoToast.success(`Retried — ${res.dispatched} sent, ${res.failed} still failing.`);
        return Promise.all([listBroadcasts(), getBroadcastRecipients(c.id)]);
      })
      .then(([list, recips]) =>
        setCampaigns(
          list.map(adaptBroadcast).map(row =>
            row.id === c.id
              ? { ...row, recipientList: recips.recipients, recipientCounts: recips.counts, recipientsTruncated: recips.truncated, recipientsLoaded: true }
              : row
          )
        )
      )
      .catch(err => toastError(err, 'Failed to retry sends.'));

  return (
    <div className='noir-broadcast-outer'>
      <CohortBuilder
        filters={filters}
        promoCodes={promoCodes}
        editors={editors}
        loading={loading}
        onPreview={preview}
        onReset={resetCohort}
      />

      <div className='nb-right'>
        {!cohort && !loading && (
          <div className='nb-card nb-empty-state'>
            <div className='nb-empty-icon'>✉</div>
            <h3 className='nb-empty-title'>Build your cohort first</h3>
            <p className='nb-empty-sub'>Use the filters on the left to define who should receive this message.</p>
          </div>
        )}

        {loading && (
          <div className='nb-card nb-loading'>
            <span className='nb-loading-inner'>
              <Spinner /> Calculating cohort…
            </span>
          </div>
        )}

        {cohort && !loading && (
          <>
            <CohortPreview
              cohort={cohort}
              filters={filters}
              brandFacets={brandFacets}
              facetsLoading={facetsLoading}
              onToggleFacetBrand={toggleFacetBrand}
              onPreview={preview}
              onOpenShopperList={() => setShowShopperList(true)}
            />

            {cohort.withPhone > 0 && (
              <MessageComposer
                cohort={cohort}
                title={title}
                setTitle={setTitle}
                msg={msg}
                setMsg={setMsg}
                curatorFilterActive={filters.curatorIds.length > 0}
                brandFilterActive={filters.brandIds.length > 0}
                mode={mode}
                setMode={setMode}
                scheduleDate={scheduleDate}
                setScheduleDate={setScheduleDate}
                scheduleTime={scheduleTime}
                setScheduleTime={setScheduleTime}
                sendState={sendState}
                setSendState={setSendState}
                onSend={send}
                testPhone={testPhone}
                setTestPhone={setTestPhone}
                testState={testState}
                setTestState={setTestState}
                testError={testError}
                setTestError={setTestError}
                onSendTest={sendTest}
              />
            )}
          </>
        )}

        <PastBroadcastsLog
          campaigns={campaigns}
          setCampaigns={setCampaigns}
          onEditBroadcast={openEditBroadcast}
          onCancelBroadcast={cancelScheduledBroadcast}
          onDuplicate={duplicateBroadcast}
          onRetryFailed={retryFailed}
        />
      </div>

      {showShopperList && cohort && (
        <ShopperListSlideOver cohort={cohort} onClose={() => setShowShopperList(false)} />
      )}

      {editingBroadcast && (
        <EditBroadcastSlideOver
          editingBroadcast={editingBroadcast}
          editDraft={editDraft}
          setEditDraft={setEditDraft}
          onSave={saveEditBroadcast}
          onClose={() => setEditingBroadcast(null)}
        />
      )}
    </div>
  );
};

export default NoirBroadcast;
